import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";
import { toDateTime } from "Helpers/Format";

import UseGlobalConfirm from "Hooks/UseGlobalConfirm";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import * as S from "./styles";

// Reenvio do pedido de validação ao proponente (11/08/2026).
//
// O pedido sai UMA vez, sozinho, quando o admin move o vínculo para "Em
// validação". Este botão é o "de novo" — para quando o proponente perde o
// e-mail. Por isso ele:
//
// - só aparece com `sentAt` preenchido (sem pedido anterior, o caminho é mudar o
//   status, não insistir);
// - sempre confirma antes, citando a data do último envio (é e-mail para
//   terceiro: um clique sem querer vira incômodo para o proponente).
const ResendValidationButton = ({ sponsorId, sentAt }) => {
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { addGlobalConfirm } = UseGlobalConfirm();

  // Data em estado próprio: depois de reenviar, a confirmação seguinte precisa
  // citar o envio novo, e a linha da tabela não é recarregada.
  const [lastSentAt, setLastSentAt] = useState(sentAt || null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLastSentAt(sentAt || null);
  }, [sentAt]);

  if (!lastSentAt || !sponsorId) return null;

  const resend = () => {
    setSending(true);

    api
      .post("/projects/resend-validation-request", {
        project_sponsor_id: sponsorId,
      })
      .then(({ data }) => {
        setLastSentAt(data?.sent_at || new Date().toISOString());
        showNotification(
          "success",
          "Sucesso!",
          data?.message || "Pedido de validação reenviado ao proponente.",
        );
      })
      .catch((error) => HTTPErrorCallback(error))
      .finally(() => setSending(false));
  };

  const confirm = () =>
    addGlobalConfirm({
      type: "critical",
      title: "Reenviar o pedido de validação?",
      message: `O proponente já recebeu este pedido em ${toDateTime(
        lastSentAt,
      )}. O status do vínculo não muda.`,
      confirmText: "Sim, reenviar",
      denyText: "Não, cancelar",
      onClickConfirm: resend,
    });

  return (
    <S.Button
      type="button"
      onClick={confirm}
      disabled={sending}
      // O texto acessível vive no aria-label; o `title` é só a dica do mouse,
      // com a data que o admin precisa para decidir.
      aria-label="Reenviar pedido de validação ao proponente"
      title={`Último envio em ${toDateTime(lastSentAt)}`}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m2 7 10 6 10-6" />
      </svg>
    </S.Button>
  );
};

export default ResendValidationButton;
